<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  isAdminHealth,
  isAdminUserUsage,
  isUsageSummary,
  type AdminHealth,
  type AdminUserUsage,
  type UsageSummary,
} from '@calloneer/contracts';
import { apiRequest, isNull } from '../api.js';
const users = ref<readonly AdminUserUsage[]>([]);
const usage = ref<readonly UsageSummary[]>([]);
const health = ref<AdminHealth>({ pendingJobs: 0, failedJobs: 0, reconciliationLag: 0 });
const isUsers = (value: unknown): value is readonly AdminUserUsage[] =>
  Array.isArray(value) && value.every(isAdminUserUsage);
const isUsage = (value: unknown): value is readonly UsageSummary[] =>
  Array.isArray(value) && value.every(isUsageSummary);
const load = async (): Promise<void> => {
  [users.value, usage.value, health.value] = await Promise.all([
    apiRequest({ path: '/v1/admin/users', guard: isUsers }),
    apiRequest({ path: '/v1/admin/usage', guard: isUsage }),
    apiRequest({ path: '/v1/admin/health', guard: isAdminHealth }),
  ]);
};
onMounted(load);
const changeRole = async (user: AdminUserUsage): Promise<void> => {
  await apiRequest({
    path: '/v1/admin/roles',
    method: 'PUT',
    body: { userId: user.userId, role: user.role === 'SUPERADMIN' ? 'USER' : 'SUPERADMIN' },
    guard: isNull,
  });
  await load();
};
</script>

<template>
  <section>
    <p class="eyebrow">REDACTED OPERATIONS</p>
    <h1>Platform administration</h1>
    <div class="stat-grid">
      <article class="panel stat">
        <p>Pending jobs</p>
        <strong>{{ health.pendingJobs }}</strong>
      </article>
      <article class="panel stat">
        <p>Failed jobs</p>
        <strong>{{ health.failedJobs }}</strong>
      </article>
      <article class="panel stat">
        <p>Reconciliation lag</p>
        <strong>{{ health.reconciliationLag }}</strong>
      </article>
      <article v-for="item in usage" :key="item.currency" class="panel stat">
        <p>Total cost · {{ item.currency }}</p>
        <strong>{{ item.cost }}</strong>
      </article>
    </div>
    <section class="panel">
      <h2>Users and aggregate usage</h2>
      <p class="fine-print">
        This view has no call IDs, destinations, caller IDs, device data, provider identifiers, or
        credentials.
      </p>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Integration</th>
              <th>Aggregate usage</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.userId">
              <td>
                <strong>{{ user.displayName }}</strong
                ><br /><span class="muted">{{ user.email }}</span>
              </td>
              <td>{{ user.role }}</td>
              <td>{{ user.integrationStatus }}</td>
              <td>
                <span v-for="item in user.usage" :key="item.currency"
                  >{{ item.callCount }} calls · {{ item.cost }} {{ item.currency }}</span
                ><span v-if="user.usage.length === 0">None</span>
              </td>
              <td>
                <button class="text-button" @click="changeRole(user)">
                  {{ user.role === 'SUPERADMIN' ? 'Revoke admin' : 'Make admin' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </section>
</template>
